import { useLocation, useNavigate } from "react-router-dom";
import styles from "./PaymentFailurePage.module.css";
import Button from "../../components/UI/Button/Button";

const getCurrencySymbol = (currency) => {
  switch (currency?.toLowerCase()) {
    case "eur":
      return "€";
    case "ron":
      return "RON";
    default:
      return "€";
  }
};

export default function PaymentFailurePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const errorData = location.state ?? null;

  const handleRetryPayment = () => {
    navigate("/billing");
  };

  const handleGoToPortal = () => {
    navigate("/portal");
  };

  const handleContactSupport = () => {
    navigate("/support");
  };

  const getErrorSuggestions = (error) => {
    const suggestions = [];

    if (error?.includes("card")) {
      suggestions.push("Check your card details and try again");
      suggestions.push("Ensure your card has sufficient funds");
      suggestions.push("Try using a different payment method");
    }

    if (error?.includes("network") || error?.includes("connection")) {
      suggestions.push("Check your internet connection");
      suggestions.push("Try refreshing the page and attempting payment again");
    }

    if (error?.includes("declined")) {
      suggestions.push("Contact your bank to authorize the payment");
      suggestions.push("Try using a different card");
    }

    if (suggestions.length === 0) {
      suggestions.push("Double-check your payment information");
      suggestions.push("Try using a different payment method");
      suggestions.push("Contact your bank if the issue persists");
    }

    return suggestions;
  };

  return (
    <div className={styles.failurePage}>
      <div className={styles.container}>
        <div className={styles.failureCard}>
          <div className={styles.iconContainer}>
            <div className={styles.errorIcon}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <circle cx="12" cy="12" r="10" fill="var(--c-danger)" />
                <path
                  d="M15 9l-6 6M9 9l6 6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className={styles.messageContainer}>
            <h1 className={styles.title}>Payment Failed</h1>
            <p className={styles.subtitle}>
              We couldn&apos;t process your payment. Don&apos;t worry, no
              charges were made to your account.
            </p>
          </div>

          {errorData ? (
            <div className={styles.errorDetails}>
              <h2 className={styles.detailsTitle}>Error Details</h2>
              <div className={styles.errorMessage}>
                <p>
                  {errorData.error ||
                    "An unexpected error occurred during payment processing."}
                </p>
              </div>

              {errorData.domain ? (
                <div className={styles.paymentInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Domain:</span>
                    <span className={styles.infoValue}>{errorData.domain}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Amount:</span>
                    <span className={styles.infoValue}>
                      {getCurrencySymbol(errorData.currency)}{" "}
                      {Number(errorData.amount).toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className={styles.suggestions}>
            <h3 className={styles.suggestionsTitle}>What you can do:</h3>
            <ul className={styles.suggestionsList}>
              {getErrorSuggestions(errorData?.error).map(
                (suggestion, index) => (
                  <li key={index}>
                    {"\u{1F4A1}"} {suggestion}
                  </li>
                )
              )}
            </ul>
          </div>

          <div className={styles.commonIssues}>
            <h3 className={styles.commonIssuesTitle}>
              Common Issues & Solutions
            </h3>
            <div className={styles.issuesGrid}>
              <div className={styles.issueCard}>
                <div className={styles.issueIcon}>{"\u{1F4B3}"}</div>
                <div className={styles.issueContent}>
                  <h4>Card Declined</h4>
                  <p>Contact your bank or try a different card</p>
                </div>
              </div>
              <div className={styles.issueCard}>
                <div className={styles.issueIcon}>{"\u{1F512}"}</div>
                <div className={styles.issueContent}>
                  <h4>Security Check</h4>
                  <p>Your bank may require additional verification</p>
                </div>
              </div>
              <div className={styles.issueCard}>
                <div className={styles.issueIcon}>{"\u{1F4B0}"}</div>
                <div className={styles.issueContent}>
                  <h4>Insufficient Funds</h4>
                  <p>Ensure your account has enough balance</p>
                </div>
              </div>
              <div className={styles.issueCard}>
                <div className={styles.issueIcon}>{"\u{1F310}"}</div>
                <div className={styles.issueContent}>
                  <h4>Network Error</h4>
                  <p>Check your connection and try again</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <Button type="button" onClick={handleRetryPayment}>
              {"\u{1F504}"} Try Again
            </Button>
            <Button type="button" onClick={handleContactSupport}>
              {"\u{1F4DE}"} Contact Support
            </Button>
            <Button type="button" onClick={handleGoToPortal}>
              Go to Portal
            </Button>
          </div>

          <div className={styles.supportInfo}>
            <p className={styles.supportText}>
              Still having trouble? Our support team is here to help!
            </p>
            <div className={styles.contactOptions}>
              <a
                href="mailto:support@softindex.ro"
                className={styles.contactLink}
              >
                {"\u{1F4E7}"} support@softindex.ro
              </a>
              <a href="tel:+40123456789" className={styles.contactLink}>
                {"\u{1F4DE}"} +40 123 456 789
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
